import React from "react";
import LegalLayout from "./LegalLayout";
import { BRAND } from "@/lib/api";

export default function Privacy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      path="/privacy"
      subtitle="How True North Maintenance collects, uses and protects your information when you use our website and services."
      lastUpdated="13 May 2026"
    >
      <p>
        True North Maintenance ("we", "us", "our") respects your privacy and is committed
        to protecting the personal information you share with us. This policy explains what
        we collect, why we collect it, how we use it, and the choices you have. It complies
        with the Australian Privacy Principles (APPs) under the <em>Privacy Act 1988</em> (Cth).
      </p>

      <h2>Who we are</h2>
      <p>
        True North Maintenance is a property maintenance and handyman business operating in
        Albury, Wodonga and surrounds (within a 60 km radius). You can contact us at{" "}
        <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a> or {BRAND.phone}.
      </p>

      <h2>What information we collect</h2>
      <ul>
        <li><strong>Contact details</strong> you provide when you request a quote, leave a review, contact us or subscribe to our email list — name, phone number, email address and the address of the property where the work will be done.</li>
        <li><strong>Job details</strong> you share with us, including photos of the work site and any free-text description of the job.</li>
        <li><strong>Review content</strong> — your name, rating, the text of your review and (optionally) the service performed.</li>
        <li><strong>Email subscription data</strong> — the email address you provided and the date you subscribed.</li>
        <li><strong>Automatically collected data</strong> — your IP address (anonymised), browser type, the pages you visit, and how you arrived at our site. We use this only to understand traffic patterns and improve the website.</li>
        <li><strong>Cookies and similar technologies</strong> — we use a small number of strictly-necessary cookies to keep you signed in to the admin area and to remember your preferences. We do not use third-party advertising or tracking cookies.</li>
      </ul>

      <h2>How we use your information</h2>
      <ul>
        <li>To respond to your quote, contact and review submissions.</li>
        <li>To schedule, perform and follow up on the work you have asked us to do.</li>
        <li>To send you email updates only if you have explicitly subscribed via our website — including seasonal maintenance tips and off-peak specials. You can unsubscribe at any time using the link in any of our emails.</li>
        <li>To publish reviews on our website with your consent (you decide whether to also leave a Google review).</li>
        <li>To comply with legal obligations (e.g. invoicing, tax records).</li>
      </ul>

      <h2>How we store and protect your information</h2>
      <p>
        Your information is stored in secure cloud infrastructure with industry-standard
        encryption in transit (TLS 1.2+) and at rest. Only authorised staff at True North
        Maintenance can access your personal information. We never sell, rent or trade
        your data with third parties for marketing purposes.
      </p>

      <h2>Third parties we share with</h2>
      <p>We only share data with the following service providers, and only as much as is required for them to perform their function:</p>
      <ul>
        <li><strong>Google</strong> — we use Google Business Profile, Google Maps and Google Search Console to manage our listing and analytics. Reviews you leave on Google are governed by Google's own Privacy Policy.</li>
        <li><strong>Meta (Facebook &amp; Instagram)</strong> — if we feature your project on our social channels with your permission, your content may be visible publicly on those platforms.</li>
        <li><strong>Emergent</strong> — hosts our website. They do not have access to or use your personal data.</li>
      </ul>

      <h2>Your rights</h2>
      <p>Under the Australian Privacy Principles you have the right to:</p>
      <ul>
        <li>Access the personal information we hold about you.</li>
        <li>Ask us to correct any inaccurate information.</li>
        <li>Ask us to delete your information (see our <a href="/data-deletion">Data Deletion page</a>).</li>
        <li>Withdraw consent to marketing at any time by clicking the unsubscribe link.</li>
        <li>Lodge a complaint with the Office of the Australian Information Commissioner (OAIC) if you feel your privacy has been breached.</li>
      </ul>

      <h2>How long we keep your information</h2>
      <p>
        We keep customer information for as long as you are an active client plus seven (7)
        years after our last interaction (for tax and warranty purposes). Email subscribers
        are kept until you unsubscribe. Reviews remain published until you ask us to remove
        them.
      </p>

      <h2>Children</h2>
      <p>Our services are not directed to anyone under 18 and we do not knowingly collect data from minors.</p>

      <h2>Changes to this policy</h2>
      <p>
        We may update this policy from time to time. Any material changes will be posted on this
        page with a new "last updated" date. Continued use of our services after a change
        constitutes acceptance of the updated policy.
      </p>

      <h2>Contact</h2>
      <p>
        Privacy questions or requests: <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a>
      </p>
    </LegalLayout>
  );
}
